import { apiFetch } from "../api/client";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import API from "../config";

const STATUSES = [
  "new",
  "triage",
  "investigating",
  "contained",
  "remediating",
  "resolved",
  "closed",
  "false_positive",
];

function formatDate(value) {
  if (!value) return "Not recorded";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString();
}

function IncidentDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [incident, setIncident] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [comments, setComments] = useState([]);

  const [commentBody, setCommentBody] = useState("");
  const [newStatus, setNewStatus] = useState("");
  const [statusReason, setStatusReason] = useState("");

  const [analystName, setAnalystName] = useState("");
  const [teamName, setTeamName] = useState("");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [edrActions, setEdrActions] = useState([]);
  const [edrActionLoading, setEdrActionLoading] = useState(false);

  const loadIncident = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const [incidentResponse, timelineResponse, commentsResponse] =
        await Promise.all([
          apiFetch(`/incidents/${id}`),
          apiFetch(`/incidents/${id}/timeline`),
          apiFetch(`/incidents/${id}/comments`),
        ]);

      if (!incidentResponse.ok) {
        throw new Error("Incident not found");
      }

      const incidentData = await incidentResponse.json();
      const timelineData = timelineResponse.ok
        ? await timelineResponse.json()
        : [];
      const commentsData = commentsResponse.ok
        ? await commentsResponse.json()
        : [];

      setIncident(incidentData);
      setTimeline(Array.isArray(timelineData) ? timelineData : []);
      setComments(Array.isArray(commentsData) ? commentsData : []);
      setNewStatus(incidentData.status || "new");

      if (incidentData.device_uuid) {
        const edrResponse = await apiFetch(`/edr/actions?device_uuid=${encodeURIComponent(
            incidentData.device_uuid
          )}&limit=25`
        );

        const edrData = edrResponse.ok ? await edrResponse.json() : [];
        setEdrActions(Array.isArray(edrData) ? edrData : []);
      } else {
        setEdrActions([]);
      }
      setTeamName(incidentData.assigned_team || "");
    } catch (loadError) {
      console.error("Incident details load error:", loadError);
      setError(loadError.message || "Unable to load incident.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadIncident();
  }, [loadIncident]);

  const addComment = async (event) => {
    event.preventDefault();

    if (!commentBody.trim()) return;

    try {
      setSaving(true);

      const response = await apiFetch(`/incidents/${id}/comment`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          author_name:
            localStorage.getItem("username") || "SOC Administrator",
          author_role: localStorage.getItem("role") || "SOC Analyst",
          comment_type: "internal",
          body: commentBody.trim(),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || "Failed to add comment");
      }

      setCommentBody("");
      await loadIncident();
    } catch (commentError) {
      alert(commentError.message);
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async () => {
    try {
      setSaving(true);

      const response = await apiFetch(`/incidents/${id}/status`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          status: newStatus,
          changed_by:
            localStorage.getItem("username") || "SOC Administrator",
          reason: statusReason.trim() || "Status updated from analyst console.",
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || "Failed to update status");
      }

      setStatusReason("");
      await loadIncident();
    } catch (statusError) {
      alert(statusError.message);
    } finally {
      setSaving(false);
    }
  };

  const downloadIncidentPdf = () => {
    if (!incident?.id) return;

    window.open(
      `${API}/reporting/incidents/${incident.id}.pdf`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const openIncidentReportJson = () => {
    if (!incident?.id) return;

    window.open(
      `${API}/reporting/incidents/${incident.id}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const requestEdrAction = async (actionType) => {
    if (!incident.device_uuid) {
      alert("This incident is not linked to an endpoint.");
      return;
    }

    try {
      setEdrActionLoading(true);

      const response = await apiFetch(`/edr/actions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          device_uuid: incident.device_uuid,
          incident_id: incident.id,
          action_type: actionType,
          priority: "normal",
          requested_by:
            localStorage.getItem("username") || "SOC Administrator",
          requested_by_role:
            localStorage.getItem("role") || "SOC Analyst",
          timeout_seconds: actionType === "run_defender_quick_scan" ? 900 : 300,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || "Failed to create EDR action");
      }

      await loadIncident();
    } catch (actionError) {
      alert(actionError.message);
    } finally {
      setEdrActionLoading(false);
    }
  };

  const assignIncident = async () => {
    if (!analystName.trim()) {
      alert("Enter an analyst name.");
      return;
    }

    try {
      setSaving(true);

      const response = await apiFetch(`/incidents/${id}/assign`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          analyst_name: analystName.trim(),
          team_name: teamName.trim() || null,
          assigned_by:
            localStorage.getItem("username") || "SOC Administrator",
          assignment_reason: "Assigned from the SOC analyst console.",
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || "Failed to assign incident");
      }

      setAnalystName("");
      await loadIncident();
    } catch (assignmentError) {
      alert(assignmentError.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="incident-details-loading">Loading incident...</div>;
  }

  if (error || !incident) {
    return (
      <div className="incident-details-error">
        <p>{error || "Incident not found."}</p>
        <button type="button" onClick={() => navigate("/incidents")}>
          Return to Incident Queue
        </button>
      </div>
    );
  }

  return (
    <div className="incident-details-page">
      <div className="incident-report-toolbar">
        <div>
          <span>Case reporting</span>
          <strong>
            {incident?.incident_number || "Incident Report"}
          </strong>
        </div>

        <div className="incident-report-toolbar-actions">
          <button
            type="button"
            onClick={openIncidentReportJson}
          >
            Open Report Data
          </button>

          <button
            type="button"
            className="incident-report-pdf-button"
            onClick={downloadIncidentPdf}
          >
            Download PDF Report
          </button>
        </div>
      </div>
      <section className="incident-details-hero">
        <div>
          <button
            type="button"
            className="incident-back-button"
            onClick={() => navigate("/incidents")}
          >
            ← Incident Queue
          </button>

          <div className="incident-number">
            {incident.incident_number || `Incident ${incident.id}`}
          </div>

          <h2>{incident.title}</h2>

          <p>{incident.description || "No incident description provided."}</p>
        </div>

        <div className="incident-hero-badges">
          <span className={`incident-priority ${incident.priority?.toLowerCase()}`}>
            {incident.priority}
          </span>

          <span className={`incident-severity ${incident.severity}`}>
            {incident.severity}
          </span>

          <span className="incident-status-badge">{incident.status}</span>
        </div>
      </section>

      <section className="incident-details-grid">
        <div className="incident-main-column">
          <div className="incident-panel">
            <div className="incident-panel-heading">
              <div>
                <span>Investigation</span>
                <h3>Incident Timeline</h3>
              </div>
            </div>

            <div className="incident-timeline">
              {timeline.length > 0 ? (
                timeline.map((event) => (
                  <div className="incident-timeline-item" key={event.id}>
                    <div className="incident-timeline-marker" />

                    <div className="incident-timeline-content">
                      <div className="incident-timeline-header">
                        <strong>{event.title || event.event_type}</strong>
                        <span>{formatDate(event.created_at)}</span>
                      </div>

                      {event.description && <p>{event.description}</p>}

                      <small>
                        {event.actor_name || "Wireless Press SOC"} ·{" "}
                        {event.event_source || "soc"}
                      </small>
                    </div>
                  </div>
                ))
              ) : (
                <div className="incident-empty">
                  No investigation events recorded.
                </div>
              )}
            </div>
          </div>

          <div className="incident-panel">
            <div className="incident-panel-heading">
              <div>
                <span>Analyst notes</span>
                <h3>Investigation Comments</h3>
              </div>
            </div>

            <div className="incident-comments-list">
              {comments.length > 0 ? (
                comments.map((comment) => (
                  <div className="incident-comment" key={comment.id}>
                    <div className="incident-comment-header">
                      <strong>{comment.author_name || "SOC Analyst"}</strong>
                      <span>{formatDate(comment.created_at)}</span>
                    </div>

                    <p>{comment.body}</p>

                    <small>
                      {comment.author_role || "Analyst"} ·{" "}
                      {comment.comment_type || "internal"}
                    </small>
                  </div>
                ))
              ) : (
                <div className="incident-empty">
                  No investigation comments yet.
                </div>
              )}
            </div>

            <form onSubmit={addComment} className="incident-comment-form">
              <textarea
                value={commentBody}
                onChange={(event) => setCommentBody(event.target.value)}
                placeholder="Add investigation note..."
                rows="5"
              />

              <button type="submit" disabled={saving || !commentBody.trim()}>
                {saving ? "Saving..." : "Add Investigation Note"}
              </button>
            </form>
          </div>
        </div>

        <aside className="incident-side-column">
          <div className="incident-panel">
            <div className="incident-panel-heading">
              <div>
                <span>Case details</span>
                <h3>Incident Context</h3>
              </div>
            </div>

            <dl className="incident-context-list">
              <div>
                <dt>Customer</dt>
                <dd>{incident.company_name || "Unassigned"}</dd>
              </div>

              <div>
                <dt>Endpoint</dt>
                <dd>
                  {incident.device_hostname ||
                    incident.device_uuid ||
                    "No endpoint"}
                </dd>
              </div>

              <div>
                <dt>Source</dt>
                <dd>{incident.source || "manual"}</dd>
              </div>

              <div>
                <dt>Category</dt>
                <dd>{incident.category || "uncategorized"}</dd>
              </div>

              <div>
                <dt>Created</dt>
                <dd>{formatDate(incident.created_at)}</dd>
              </div>

              <div>
                <dt>Last Updated</dt>
                <dd>{formatDate(incident.updated_at)}</dd>
              </div>

              <div>
                <dt>Acknowledged</dt>
                <dd>{formatDate(incident.acknowledged_at)}</dd>
              </div>

              <div>
                <dt>Resolution Due</dt>
                <dd>{formatDate(incident.sla_resolve_due_at)}</dd>
              </div>
            </dl>
          </div>

          <div className="incident-panel">
            <div className="incident-panel-heading">
              <div>
                <span>Workflow</span>
                <h3>Update Status</h3>
              </div>
            </div>

            <select
              value={newStatus}
              onChange={(event) => setNewStatus(event.target.value)}
            >
              {STATUSES.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>

            <textarea
              value={statusReason}
              onChange={(event) => setStatusReason(event.target.value)}
              placeholder="Reason for status change..."
              rows="3"
            />

            <button
              type="button"
              disabled={saving}
              onClick={updateStatus}
            >
              Update Incident Status
            </button>
          </div>

          <div className="incident-panel">
            <div className="incident-panel-heading">
              <div>
                <span>Endpoint response</span>
                <h3>EDR Actions</h3>
              </div>
            </div>

            <div className="incident-edr-action-grid">
              <button
                type="button"
                disabled={edrActionLoading || !incident.device_uuid}
                onClick={() => requestEdrAction("collect_processes")}
              >
                Collect Processes
              </button>

              <button
                type="button"
                disabled={edrActionLoading || !incident.device_uuid}
                onClick={() =>
                  requestEdrAction("collect_network_connections")
                }
              >
                Collect Network Connections
              </button>

              <button
                type="button"
                disabled={edrActionLoading || !incident.device_uuid}
                onClick={() => requestEdrAction("refresh_policy")}
              >
                Refresh Policy
              </button>

              <button
                type="button"
                disabled={edrActionLoading || !incident.device_uuid}
                onClick={() =>
                  requestEdrAction("run_defender_quick_scan")
                }
              >
                Run Defender Quick Scan
              </button>
            </div>

            <div className="incident-edr-actions-list">
              {edrActions.length > 0 ? (
                edrActions.map((action) => (
                  <div className="incident-edr-action-item" key={action.id}>
                    <div>
                      <strong>{action.action_type}</strong>
                      <span>{action.status}</span>
                    </div>

                    <small>{formatDate(action.requested_at)}</small>

                    {action.error_message && (
                      <p>{action.error_message}</p>
                    )}

                    {action.result &&
                      Object.keys(action.result).length > 0 && (
                        <details>
                          <summary>View Result</summary>
                          <pre>
                            {JSON.stringify(action.result, null, 2)}
                          </pre>
                        </details>
                      )}
                  </div>
                ))
              ) : (
                <div className="incident-empty">
                  No EDR actions recorded for this endpoint.
                </div>
              )}
            </div>
          </div>

          <div className="incident-panel">
            <div className="incident-panel-heading">
              <div>
                <span>Ownership</span>
                <h3>Assign Analyst</h3>
              </div>
            </div>

            <input
              type="text"
              value={analystName}
              onChange={(event) => setAnalystName(event.target.value)}
              placeholder="Analyst name"
            />

            <input
              type="text"
              value={teamName}
              onChange={(event) => setTeamName(event.target.value)}
              placeholder="Team name, e.g. Tier 1 SOC"
            />

            <button
              type="button"
              disabled={saving}
              onClick={assignIncident}
            >
              Assign Incident
            </button>
          </div>
        </aside>
      </section>
    </div>
  );
}

export default IncidentDetailsPage;
